import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatMoney } from '@/lib/format';
import type { Sale } from '@/lib/types';

export default function WholesaleHistory() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: clients } = await supabase
        .from('clients')
        .select('id')
        .eq('is_wholesale', true);
      const ids = (clients ?? []).map((c: { id: string }) => c.id);
      if (ids.length === 0) {
        setSales([]);
        return;
      }
      const { data, error: e } = await supabase
        .from('sales')
        .select('*, customer:clients(name, wholesale_code)')
        .in('customer_id', ids)
        .order('created_at', { ascending: false })
        .limit(100);
      if (e) throw e;
      setSales((data as Sale[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <p className="text-sm text-foreground-600 mb-4">
        Ventas realizadas a clientes mayoristas.
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : sales.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-receipt-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay ventas mayoristas.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Factura</th>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Código</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Cajero</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {sales.map((s) => (
                <tr key={s.id} className="hover:bg-background-50">
                  <td className="px-4 py-3 font-mono font-semibold text-foreground-950">
                    {s.invoice_number}
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{s.customer_name ?? '—'}</td>
                  <td className="px-4 py-3 text-foreground-500 hidden md:table-cell">
                    {(s as Sale & { customer?: { wholesale_code: string | null } }).customer
                      ?.wholesale_code ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-700 hidden lg:table-cell">
                    {s.cashier_name ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-semibold text-foreground-950">
                    {formatMoney(Number(s.total), 'RD$')}
                  </td>
                  <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">
                    {s.created_at ? new Date(s.created_at).toLocaleString('es-DO') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}